import * as layout from '../../../components/markLayout.module.css';
import { MarkButton } from '../../../components/MarkControls';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import type { Step1Data } from './Step1Structure';
import type { Step2Data } from './Step2Audience';
type Props = {
    step1: Step1Data;
    step2: Step2Data;
    isSubmitting: boolean;
    shellCreated: boolean;
    submitError: string | null;
    onSubmit: () => void;
};
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className={layout.summaryRow}>
        <P noDefaultMargin strong>
            {label}
        </P>
        <div>{children}</div>
    </div>
);
const Step3Review: React.FC<Props> = ({
    step1,
    step2,
    isSubmitting,
    shellCreated,
    submitError,
    onSubmit
}) => (
    <div className={layout.form}>
        <P strong>Review your blast before creating it.</P>
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <P
                strong
                noDefaultMargin
                styles={{
                    marginBottom: '0.8rem'
                }}
            >
                Identity (locked after create) and variants
            </P>
            <Row label="Name">
                <P noDefaultMargin>{step1.name || '(empty)'}</P>
            </Row>
            <Row label="Message class">
                <Tag
                    type={step1.messageClass === 'TRANSACTIONAL' ? 'warn' : 'primary'}
                    size="small"
                >
                    {step1.messageClass}
                </Tag>
            </Row>
            <Row label="Delivery scope">
                <P noDefaultMargin>
                    {step1.deliveryScope === 'PER_EMAIL' ? 'Per email' : 'Per account'}
                </P>
            </Row>
            <Row label="Variants">
                <div>
                    {step1.variants.map((v, i) => (
                        <P key={i} noDefaultMargin size="small">
                            {v.label}: {v.ratio}%
                        </P>
                    ))}
                </div>
            </Row>
        </div>
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <P
                strong
                noDefaultMargin
                styles={{
                    marginBottom: '0.8rem'
                }}
            >
                Audience &amp; schedule (editable in DRAFT)
            </P>
            <Row label="Segment">
                <P noDefaultMargin>{step2.segmentName ?? '(none selected)'}</P>
            </Row>
            {step1.deliveryScope === 'PER_ACCOUNT' && (
                <Row label="Account recipients">
                    <P noDefaultMargin>{step2.accountRecipients}</P>
                </Row>
            )}
            <Row label="From address">
                <P noDefaultMargin>{step2.fromAddress.trim() || '(default)'}</P>
            </Row>
            <Row label="Planned schedule">
                <P noDefaultMargin>
                    {step2.scheduleMode === 'immediate'
                        ? 'Send immediately after publish'
                        : step2.scheduledAt
                          ? `Planned send time: ${step2.scheduledAt.replace('T', ' ')} UTC`
                          : 'Planned send time: (not set)'}
                </P>
            </Row>
            {step2.scheduleMode === 'scheduled' && step2.localSendTime && (
                <Row label="Local send time">
                    <P noDefaultMargin>{step2.localSendTime}</P>
                </Row>
            )}
        </div>
        <P>
            {step2.scheduleMode === 'immediate' ? (
                <>
                    <strong>Nothing is sent yet.</strong> This saves a draft. Next, open the blast,
                    add your content, then click <strong>Publish now</strong> to send it.
                </>
            ) : (
                <>
                    <strong>Nothing is scheduled yet.</strong> This saves a draft with your planned
                    time. Next, open the blast, add your content, then click{' '}
                    <strong>Schedule</strong> to confirm the time.
                </>
            )}
        </P>
        {submitError && (
            <div
                role="alert"
                style={{
                    marginBottom: '1rem'
                }}
                className={layout.notice}
            >
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-danger)'
                    }}
                >
                    {submitError}
                </P>
            </div>
        )}
        <MarkButton
            size="regular"
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            variant="primary"
        >
            {isSubmitting
                ? shellCreated
                    ? 'Saving variants...'
                    : 'Saving draft...'
                : shellCreated
                  ? 'Retry saving variants'
                  : 'Save draft'}
        </MarkButton>
    </div>
);
export default Step3Review;
